import {
    TbArrowsMove,
    TbCopy,
    TbFilePlus,
    TbFolderPlus,
    TbPencil,
    TbRestore,
    TbStack2,
    TbTrash,
    TbTrashX,
    TbUpload,
    TbUserCheck,
    TbUserMinus,
    TbUserPlus,
} from 'react-icons/tb'

import { HistoryDiff, HistoryInline, HistoryMuted } from '@/Ship/Components/History'

import { DISK_NODE_HISTORY_EVENT_MAP } from '../Contracts/DiskNodeHistoryEvent'
import { formatBytes, itemsCount } from './diskFormat'

import type { DiskHistoryItem } from '../Contracts/DiskNode'
import type { DiskNodeHistoryKind } from '../Contracts/DiskNodeHistoryEvent'
import type { HistoryEntry } from '@/Ship/Components/History'
import type { ReactNode } from 'react'

type Payload = Record<string, unknown>

const ICONS: Record<DiskNodeHistoryKind, ReactNode> = {
    created: <TbFolderPlus />,
    renamed: <TbPencil />,
    description: <TbPencil />,
    moved: <TbArrowsMove />,
    copied: <TbCopy />,
    uploaded: <TbUpload />,
    version: <TbStack2 />,
    trashed: <TbTrash />,
    restored: <TbRestore />,
    purged: <TbTrashX />,
    shared: <TbUserPlus />,
    shareChanged: <TbUserCheck />,
    unshared: <TbUserMinus />,
    transferred: <TbArrowsMove />,
}

/** Підписи ролей диска — `DiskRole::label()` бека. */
const ROLE_LABELS: Record<string, string> = {
    viewer: 'Переглядач',
    commenter: 'Коментатор',
    editor: 'Редактор',
    manager: 'Менеджер',
}

const roleLabel = (value: unknown): string | null => {
    const role = text(value)

    return role !== null ? (ROLE_LABELS[role] ?? role) : null
}

/** `2026-11-30T23:59:59+02:00` → «до 30.11.2026»; без строку — `null`. */
const untilLabel = (value: unknown): string | null => {
    const at = text(value)

    return at !== null ? `до ${at.slice(8, 10)}.${at.slice(5, 7)}.${at.slice(0, 4)}` : null
}

const text = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null)
const record = (value: unknown): Payload => (value !== null && typeof value === 'object' ? (value as Payload) : {})
const nameOf = (value: unknown): string | null => text(record(value)['name']) ?? text(record(value)['title'])

/** Подробиці події за описом `payload` у `DiskNodeHistoryEvent.php`. */
const details = (kind: DiskNodeHistoryKind, payload: Payload): ReactNode => {
    switch (kind) {
        case 'renamed':
        case 'description': {
            const from = text(payload['old'])
            const to = text(payload['new'])

            return from !== null || to !== null ? <HistoryDiff from={from ?? '—'} to={to ?? '—'} /> : null
        }
        case 'moved': {
            const from = nameOf(record(payload['old'])['parent'])
            const to = nameOf(record(payload['new'])['parent'])

            return from !== null && to !== null ? <HistoryDiff from={from} to={to} strike={false} /> : null
        }
        case 'copied': {
            const source = nameOf(payload['source'])

            return source !== null ? <HistoryMuted>Копія «{source}»</HistoryMuted> : null
        }
        case 'uploaded':
        case 'version': {
            const size = typeof payload['size'] === 'number' ? formatBytes(payload['size']) : null
            const number = typeof payload['number'] === 'number' ? `Версія ${payload['number']}` : null

            return [number, size].some(Boolean) ? (
                <HistoryInline>{[number, size].filter(Boolean).join(' · ')}</HistoryInline>
            ) : null
        }
        case 'trashed':
        case 'restored': {
            const where = nameOf(payload['parent'])
            const count =
                typeof payload['nodes'] === 'number' && payload['nodes'] > 1 ? itemsCount(payload['nodes']) : null

            return where !== null ? (
                <HistoryMuted>
                    {kind === 'trashed' ? 'З папки' : 'У папку'} «{where}»{count ? ` · ${count}` : ''}
                </HistoryMuted>
            ) : null
        }
        case 'shared':
        case 'unshared': {
            const who = text(record(payload['principal'])['title'])
            const parts = [who, roleLabel(payload['role']), untilLabel(payload['expiresAt'])]
            const expired =
                kind === 'unshared' && payload['by'] === 'expiry'
                    ? ' · минув строк'
                    : kind === 'unshared' && payload['by'] === 'offboarding'
                      ? ' · співробітника звільнено'
                      : kind === 'shared' && payload['by'] === 'transfer'
                        ? ' · закріплено при передачі'
                        : ''

            return parts.some(Boolean) ? (
                <HistoryInline>{`${parts.filter(Boolean).join(' · ')}${expired}`}</HistoryInline>
            ) : null
        }
        case 'transferred': {
            // Від кого й кому: власник особистого диска або назва простору (відділ)
            const side = (value: unknown): string | null =>
                nameOf(record(value)['owner']) ?? nameOf(record(value)['storage'])
            const from = side(payload['from'])
            const to = side(payload['to'])
            const note = payload['revert'] === true ? 'повернуто' : payload['by'] === 'policy' ? 'за політикою' : null

            return (
                <>
                    {from !== null && to !== null ? <HistoryDiff from={from} to={to} strike={false} /> : null}
                    {note !== null ? <HistoryMuted>{note}</HistoryMuted> : null}
                </>
            )
        }
        case 'shareChanged': {
            const who = text(record(payload['principal'])['title'])
            const role = record(payload['role'])
            const expires = record(payload['expiresAt'])
            const roleFrom = roleLabel(role['old'])
            const roleTo = roleLabel(role['new'])
            const untilFrom = untilLabel(expires['old']) ?? 'без строку'
            const untilTo = untilLabel(expires['new']) ?? 'без строку'

            return (
                <>
                    {who !== null ? <HistoryMuted>{who}</HistoryMuted> : null}
                    {roleFrom !== roleTo ? <HistoryDiff from={roleFrom ?? '—'} to={roleTo ?? '—'} /> : null}
                    {untilFrom !== untilTo ? <HistoryDiff from={untilFrom} to={untilTo} /> : null}
                </>
            )
        }
        case 'purged': {
            const name = text(payload['name'])

            return name !== null ? <HistoryMuted>«{name}»</HistoryMuted> : null
        }
        default:
            return null
    }
}

/** Журнал вузла диска → рядки спільної стрічки `HistoryTimeline`. */
export const diskHistoryEntries = (items: DiskHistoryItem[]): HistoryEntry[] =>
    items.map((item) => {
        const info = DISK_NODE_HISTORY_EVENT_MAP[item.event]
        const kind = info?.kind

        return {
            key: item.uuid,
            title: info?.label ?? 'Зміна',
            icon: kind ? ICONS[kind] : <TbFilePlus />,
            tone: info?.tone ?? 'neutral',
            actor: item.changedBy,
            occurredAt: item.occurredAt ?? undefined,
            details: kind ? details(kind, item.payload) : null,
        }
    })
